/**
 * Tiling representation for placing piece configurations on a grid
 * Format: "Piece(x,y,rotation,mirrored)[,Piece(x,y,rotation,mirrored)]*"
 * Example: "O(0,0,0,0),I(2,0,1,1),L(4,1,2,0)"
 */
import { PIECE_DEFINITIONS, getShapeForPiece } from './tetromino';
/**
 * Parse a tiling string into an array of pieces
 * Format: "O(0,0,0,0),I(2,0,1,1)"
 * Returns array sorted by (x, y) position
 */
export function parseTiling(tilingStr) {
    if (!tilingStr || tilingStr.trim() === '') {
        return [];
    }
    const pieces = [];
    const piecePattern = /([A-Z])\((\d+),(\d+),(\d),([01])\)/g;
    let match;
    while ((match = piecePattern.exec(tilingStr)) !== null) {
        const type = match[1];
        const gridX = parseInt(match[2], 10);
        const gridY = parseInt(match[3], 10);
        const rotation = parseInt(match[4], 10);
        const mirrored = match[5] === '1';
        pieces.push({ type, gridX, gridY, rotation, mirrored });
    }
    // Sort by position for canonical form
    pieces.sort((a, b) => {
        if (a.gridY !== b.gridY)
            return a.gridY - b.gridY;
        return a.gridX - b.gridX;
    });
    return pieces;
}
/**
 * Convert an array of pieces to a tiling string
 * Automatically sorts by (x, y) position for canonical form
 */
export function stringifyTiling(pieces) {
    if (pieces.length === 0) {
        return '';
    }
    // Sort by position for canonical form
    const sorted = [...pieces].sort((a, b) => {
        if (a.gridY !== b.gridY)
            return a.gridY - b.gridY;
        return a.gridX - b.gridX;
    });
    const tilingStr = sorted
        .map(p => `${p.type}(${p.gridX},${p.gridY},${p.rotation},${p.mirrored ? 1 : 0})`)
        .join(',');
    return tilingStr;
}
/**
 * Validate that pieces don't overlap on the grid
 */
export function isValidTiling(tilingStr, mapWidth, mapHeight) {
    const pieces = parseTiling(tilingStr);
    const occupied = new Set();
    for (const piece of pieces) {
        const shape = getShapeForPiece({
            type: piece.type,
            rotations: PIECE_DEFINITIONS[piece.type],
            currentRotation: piece.rotation,
            mirrored: piece.mirrored,
        });
        for (const c of shape.cells) {
            const ax = piece.gridX + c.x;
            const ay = piece.gridY + c.y;
            if (ax < 0 || ax >= mapWidth || ay < 0 || ay >= mapHeight)
                return false;
            const key = `${ax},${ay}`;
            if (occupied.has(key))
                return false;
            occupied.add(key);
        }
    }
    return true;
}
/**
 * Export tiling to JSON format for storage/sharing
 */
export function exportTilingJson(tilingStr, mapWidth, mapHeight) {
    const pieces = parseTiling(tilingStr);
    const data = {
        version: '1.0',
        mapWidth,
        mapHeight,
        tiling: tilingStr,
        pieces: pieces.length,
        createdAt: new Date().toISOString(),
    };
    return JSON.stringify(data, null, 2);
}
/**
 * Import tiling from JSON format
 */
export function importTilingJson(jsonStr) {
    const data = JSON.parse(jsonStr);
    if (!data.tiling || !data.mapWidth || !data.mapHeight) {
        throw new Error('Invalid tiling JSON: missing required fields (tiling, mapWidth, mapHeight)');
    }
    return {
        tiling: data.tiling,
        mapWidth: data.mapWidth,
        mapHeight: data.mapHeight,
    };
}
/**
 * Export tiling to CSV format for easy viewing/editing
 */
export function exportTilingCsv(tilingStr) {
    const pieces = parseTiling(tilingStr);
    const lines = ['Type,X,Y,Rotation,Mirrored'];
    for (const piece of pieces) {
        lines.push(`${piece.type},${piece.gridX},${piece.gridY},${piece.rotation},${piece.mirrored ? 1 : 0}`);
    }
    return lines.join('\n');
}
/**
 * Import tiling from CSV format
 */
export function importTilingCsv(csvStr) {
    const lines = csvStr.trim().split('\n');
    if (lines.length < 2) {
        throw new Error('Invalid CSV: must have header and at least one piece');
    }
    const pieces = [];
    for (let i = 1; i < lines.length; i++) {
        const parts = lines[i].split(',');
        if (parts.length !== 5) {
            throw new Error(`Invalid CSV line ${i}: expected 5 columns`);
        }
        const [type, x, y, rotation, mirrored] = parts;
        pieces.push({
            type: type,
            gridX: parseInt(x, 10),
            gridY: parseInt(y, 10),
            rotation: parseInt(rotation, 10),
            mirrored: mirrored === '1',
        });
    }
    return stringifyTiling(pieces);
}
//# sourceMappingURL=tiling.js.map